import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1682954100067-c4356f636c6b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwzfHxjcm9jaGV0JTIweWFybiUyMHRleHR1cmV8ZW58MHx8fHwxNzkxNzU3MjQzfDA&ixlib=rb-4.1.0&q=85";

const lines = ["Soft stitches,", "slow made,", "loved forever."];

const RevealLine = ({ text, delay, accent }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
      className={`block font-heading font-medium tracking-tight text-5xl sm:text-6xl lg:text-7xl ${
        accent ? "text-[#C27F67]" : "text-[#2C402B]"
      }`}
    >
      {text}
    </motion.span>
  </span>
);

const Hero = ({ onOrder }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, -90]);
  const imgRotate = useTransform(scrollYProgress, [0, 1], [0, 6]);

  return (
    <section
      id="top"
      ref={ref}
      className="relative min-h-screen flex items-center pt-28 pb-16 overflow-hidden"
      data-testid="hero-section"
    >
      <div className="absolute -top-24 -right-24 w-[28rem] h-[28rem] rounded-full bg-[#E8EAE3] blur-3xl opacity-70 pointer-events-none" />
      <div className="max-w-6xl mx-auto px-6 w-full grid lg:grid-cols-2 gap-16 items-center">
        <div>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15, duration: 0.7 }}
            className="font-hand text-2xl text-[#A67C52] -rotate-2 mb-4"
            data-testid="hero-eyebrow"
          >
            welcome to Crochet Els
          </motion.p>
          <h1 data-testid="hero-heading">
            {lines.map((line, i) => (
              <RevealLine key={line} text={line} delay={0.35 + i * 0.16} accent={i === 2} />
            ))}
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.0, duration: 0.8 }}
            className="mt-6 text-base sm:text-lg text-[#576956] leading-relaxed max-w-md"
            data-testid="hero-subtext"
          >
            Handmade crochet scrunchies and bandanas, stitched one loop at a
            time with cozy cotton yarn and a whole lot of love.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.2, duration: 0.8 }}
            className="mt-10 flex flex-wrap items-center gap-5"
          >
            <button
              onClick={onOrder}
              data-testid="hero-order-button"
              className="rounded-full bg-[#2C402B] text-[#F9F6F0] px-8 py-4 font-heading font-medium transition-[transform,background-color,box-shadow] duration-300 hover:bg-[#435E42] hover:scale-105 hover:shadow-[0_14px_30px_rgba(44,64,43,0.28)]"
            >
              Place an Order
            </button>
            <a
              href="#shop"
              data-testid="hero-shop-link"
              className="flex items-center gap-2 font-heading font-medium text-[#2C402B] group"
            >
              See the goods
              <ArrowDown size={18} className="transition-transform duration-300 group-hover:translate-y-1" />
            </a>
          </motion.div>
        </div>

        <motion.div style={{ y: imgY, rotate: imgRotate }} className="relative" data-testid="hero-image-wrap">
          <motion.div
            initial={{ opacity: 0, scale: 0.92 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.5, duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <motion.div
              animate={{ y: [0, -14, 0] }}
              transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
              className="rounded-t-[999px] rounded-b-[2.5rem] overflow-hidden shadow-[0_30px_60px_rgba(44,64,43,0.18)] border-4 border-white"
            >
              <img
                src={HERO_IMG}
                alt="Earthy toned balls of yarn"
                className="w-full h-[26rem] sm:h-[32rem] object-cover"
                data-testid="hero-image"
              />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 1.3, type: "spring", stiffness: 200, damping: 14 }}
              className="absolute -bottom-6 -left-6 bg-white rounded-3xl shadow-[0_8px_30px_rgba(44,64,43,0.12)] px-6 py-4 -rotate-3"
              data-testid="hero-price-badge"
            >
              <p className="font-hand text-2xl text-[#C27F67] leading-none">scrunchies $5</p>
              <p className="font-hand text-2xl text-[#2C402B] leading-none mt-1">bandanas $10</p>
            </motion.div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
